import {Injectable, signal} from '@angular/core';
import {environment} from '@/environments/environment';

/** Период опроса доступности бэкенда. */
export const HEALTH_CHECK_INTERVAL_MS = 15000;
/** Таймаут одного запроса: дольше ждать нет смысла, следующий опрос всё равно через 15 с. */
const HEALTH_CHECK_TIMEOUT_MS = 3000;
/** Показываем уведомление только после двух последовательных неудачных проверок. */
const HEALTH_CHECK_FAILURE_THRESHOLD = 2;

/**
 * Адрес абсолютный: приложение ходит на бэкенд по другому origin, а относительный
 * `/api/health` попал бы в nginx (`try_files ... /index.html`) и всегда возвращал 200.
 */
const HEALTH_URL = `${environment.schema}://${environment.apiUrl}${environment.prefix}/health`;

/**
 * Опрос /api/health и состояние «сервер недоступен».
 *
 * Запрос идёт через fetch, а не HttpClient: иначе каждые 15 с срабатывал бы
 * loadingInterceptor и BasicAuthInterceptor (лишний спиннер и попытки refresh).
 * CORS на бэкенде должен разрешать GET без credentials.
 */
@Injectable({providedIn: 'root'})
export class HealthCheckService {
    /** Сервер (или сеть) недоступен: fetch упал по сети/таймауту либо ответил 5xx. */
    readonly serverUnavailable = signal(false);

    private timer: ReturnType<typeof setInterval> | null = null;
    private inFlight = false;

    private consecutiveFailures = 0;

    private readonly onOnline = () => this.check();
    // Само событие не считаем отдельной ошибкой:
    // проверка ниже учтёт navigator.onLine.
    private readonly onOffline = () => void this.check();
    private readonly onVisibilityChange = () => {
        if (document.visibilityState === 'visible') return this.check();
    };

    start(): void {
        if (this.timer !== null) return;

        window.addEventListener('online', this.onOnline);
        window.addEventListener('offline', this.onOffline);
        document.addEventListener('visibilitychange', this.onVisibilityChange);

        // Первый опрос сразу: иначе первое сообщение о падении пришло бы только через 15 с.
        void this.check();
        this.timer = setInterval(() => void this.check(), HEALTH_CHECK_INTERVAL_MS);
    }

    stop(): void {
        if (this.timer === null) return;

        clearInterval(this.timer);
        this.timer = null;
        this.consecutiveFailures = 0;

        window.removeEventListener('online', this.onOnline);
        window.removeEventListener('offline', this.onOffline);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);

    }

    async check(): Promise<void> {
        // Запросы не должны накладываться.
        if (this.inFlight) return;

        if (!navigator.onLine) {
            this.applyCheckResult(false);
            return;
        }

        this.inFlight = true;
        let reachable = false;

        try {
            const response = await fetch(HEALTH_URL, {
                cache: 'no-store',
                credentials: 'omit',
                signal: AbortSignal.timeout(HEALTH_CHECK_TIMEOUT_MS),
            });

            // Сохраняем текущую семантику:
            // 4xx — сервер отвечает, 5xx — недоступность.
            reachable = response.status < 500;
        } catch {
            reachable = false;
        } finally {
            this.inFlight = false;
        }

        this.applyCheckResult(reachable);
    }


    private applyCheckResult(reachable: boolean): void {
        if (reachable) {
            this.consecutiveFailures = 0;
            this.serverUnavailable.set(false);
            return;
        }

        this.consecutiveFailures = Math.min(
            this.consecutiveFailures + 1,
            HEALTH_CHECK_FAILURE_THRESHOLD,
        );

        if (this.consecutiveFailures >= HEALTH_CHECK_FAILURE_THRESHOLD) {
            this.serverUnavailable.set(true);
        }
    }

}
